import React, { useState, useEffect } from 'react';
import { ClientLayout } from '../../../components/layout/ClientLayout';
import { useCheckPermissions } from '../../../middleware/checkPermissions';
import { supabase } from '../../../lib/supabase';
import { Loader2, Search, Users, ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';
import { Input } from '../../../components/ui/input';
import { Select } from '../../../components/ui/select';
import { format, subDays, subMonths, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../../../components/ui/button';

interface Contestant {
  id: string;
  name: string;
  photo_url: string;
  unique_code: string;
  votes_count: number;
  created_at: string;
  contest: {
    id: string;
    name: string;
  };
  votes: {
    id: string;
  }[];
}

interface Stats {
  total: number;
  totalVotes: number;
  averageVotes: number;
}

export const ContestantList = () => {
  const { user } = useCheckPermissions(['view_stats']);
  const [contestants, setContestants] = useState<Contestant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState<Stats>({
    total: 0,
    totalVotes: 0,
    averageVotes: 0
  });
  const [activeTab, setActiveTab] = useState<'list' | 'links'>('list');
  const [contestFilter, setContestFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const [dateFilter, setDateFilter] = useState('all');
  const [contestOptions, setContestOptions] = useState<Array<{ value: string; label: string }>>([
    { value: 'all', label: 'Tous les concours' }
  ]);
  const [selectedContest, setSelectedContest] = useState<string | null>(null);
  const [contestsData, setContestsData] = useState<Array<{
    name: string;
    slug: string;
    code_unique?: string;
    contestants: Array<{
      name: string;
      unique_code: string;
    }>;
  }>>([]);
  const [copied, setCopied] = useState(false);
  const itemsPerPage = 20;

  const sortOptions = [
    { value: 'name_asc', label: 'Nom (A-Z)' },
    { value: 'name_desc', label: 'Nom (Z-A)' },
    { value: 'votes_asc', label: 'Votes (croissant)' },
    { value: 'votes_desc', label: 'Votes (décroissant)' },
    { value: 'date_asc', label: 'Date (ancien)' },
    { value: 'date_desc', label: 'Date (récent)' }
  ];

  const dateOptions = [
    { value: 'all', label: 'Toutes les dates' },
    { value: 'today', label: "Aujourd'hui" },
    { value: 'week', label: '7 derniers jours' },
    { value: 'month', label: '30 derniers jours' },
    { value: 'year', label: '12 derniers mois' }
  ];

  const fetchContests = async () => {
    if (!user) return;
    
    try {
      const { data: contests } = await supabase
        .from('contests')
        .select(`
          name,
          slug,
          code_unique,
          contestants (
            name,
            unique_code
          )
        `)
        .in('id', user.permissions.contests)
        .order('name');

      if (!contests) return;

      setContestsData(
        contests
          .filter(contest => contest.contestants?.length > 0)
          .map(contest => ({
            name: contest.name,
            slug: contest.slug,
            code_unique: contest.code_unique,
            contestants: contest.contestants
          }))
      );
    } catch (error) {
      console.error('Erreur lors de la récupération des concours:', error);
    }
  };

  const getContestLinks = (contestName: string) => {
    const contest = contestsData.find(c => c.name === contestName);
    if (!contest) return '';
    
    // Ajouter le lien d'inscription au concours
    const registrationLink = `https://votafrica.com/inscription/${contest.code_unique}`;
    
    return `# Lien du concours ou de la Catégorie\nhttps://votafrica.com/c/${contest.slug}\n\n# Lien d'inscription\n${registrationLink}\n\n# Liens des Candidats\n${contest.contestants
      .map((c, i) => `${i + 1}. ${c.name}: https://votafrica.com/${c.unique_code}`)
      .join('\n')}`;
  };

  const copyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        return;
      }

      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        textArea.remove();
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Erreur lors de la copie (fallback):', err);
        textArea.remove();
      }
    } catch (err) {
      console.error('Erreur lors de la copie:', err);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;

      try {
        // Récupérer les statistiques des concours avec les transactions
        const { data: contestStats, error } = await supabase
          .from('contests')
          .select(`
            id,
            name,
            contestants (
              id,
              name
            )
          `)
          .in('id', user.permissions.contests);

        if (error) {
          console.error('Error fetching contest stats:', error);
          throw error;
        }

        // Calculer les statistiques totales
        let totalContestants = 0;
        let totalVotes = 0;

        // Pour chaque concours
        for (const contest of contestStats || []) {
          // Pour chaque candidat
          for (const contestant of contest.contestants || []) {
            totalContestants++;
            
            // Récupérer le nombre exact de votes pour ce candidat
            const { count: contestantVotes } = await supabase
              .from('votes')
              .select('id', { count: 'exact', head: true })
              .eq('contestant_id', contestant.id);
            
            totalVotes += contestantVotes || 0;
          }
        }

        setStats({
          total: totalContestants,
          totalVotes: totalVotes,
          averageVotes: totalContestants ? Math.round(totalVotes / totalContestants) : 0
        });
      } catch (error) {
        console.error('Erreur lors de la récupération des statistiques:', error);
      }
    };

    fetchStats();
  }, [user]);

  useEffect(() => {
    const fetchContestants = async () => {
      if (!user) return;

      try {
        setIsLoading(true);
        
        let query = supabase
          .from('contestants')
          .select(`
            id,
            name,
            photo_url,
            unique_code,
            created_at,
            contest:contest_id (
              id,
              name
            ),
            votes:votes (
              id
            )
          `, { count: 'exact' })
          .in('contest_id', user.permissions.contests);

        // Appliquer le filtre de recherche
        if (searchTerm) {
          query = query.ilike('name', `%${searchTerm}%`);
        }

        // Appliquer le filtre de concours
        if (contestFilter !== 'all') {
          query = query.eq('contest_id', contestFilter);
        }

        // Appliquer le filtre de date
        if (dateFilter !== 'all') {
          const now = new Date();
          let startDate;
          
          switch (dateFilter) {
            case 'today':
              startDate = startOfDay(now);
              break;
            case 'week':
              startDate = subDays(now, 7);
              break;
            case 'month':
              startDate = subDays(now, 30);
              break;
            case 'year':
              startDate = subMonths(now, 12);
              break;
          }
          
          if (startDate) {
            query = query.gte('created_at', startDate.toISOString());
          }
        }

        // Appliquer le tri
        switch (sortBy) {
          case 'name_asc':
            query = query.order('name', { ascending: true });
            break;
          case 'name_desc':
            query = query.order('name', { ascending: false });
            break;
          case 'date_asc':
            query = query.order('created_at', { ascending: true });
            break;
          case 'date_desc':
            query = query.order('created_at', { ascending: false });
            break;
          // Les tris par votes seront appliqués après la récupération des données
        }

        // Pagination
        const start = (currentPage - 1) * itemsPerPage;
        const end = start + itemsPerPage - 1;
        query = query.range(start, end);

        const { data, error, count } = await query;

        if (error) throw error;

        // Calculer le nombre de votes pour chaque candidat avec la méthode COUNT exacte
        const contestantsWithVotes = await Promise.all((data || []).map(async contestant => {
          const { count: votesCount } = await supabase
            .from('votes')
            .select('id', { count: 'exact', head: true })
            .eq('contestant_id', contestant.id);

          return {
            ...contestant,
            votes_count: votesCount || 0
          };
        }));

        // Appliquer le tri par votes si nécessaire
        if (sortBy === 'votes_asc') {
          contestantsWithVotes.sort((a, b) => a.votes_count - b.votes_count);
        } else if (sortBy === 'votes_desc') {
          contestantsWithVotes.sort((a, b) => b.votes_count - a.votes_count);
        }

        setContestants(contestantsWithVotes);
        setTotalPages(Math.ceil((count || 0) / itemsPerPage));
      } catch (error) {
        console.error('Erreur lors de la récupération des candidats:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContestants();
  }, [user, searchTerm, currentPage, contestFilter, sortBy, dateFilter]);

  // Récupérer la liste des concours pour le filtre
  useEffect(() => {
    const fetchContests = async () => {
      if (!user) return;

      try {
        const { data: contests, error } = await supabase
          .from('contests')
          .select('id, name')
          .in('id', user.permissions.contests)
          .order('name');

        if (error) throw error;

        setContestOptions([
          { value: 'all', label: 'Tous les concours' },
          ...(contests || []).map(contest => ({
            value: contest.id,
            label: contest.name
          }))
        ]);
      } catch (error) {
        console.error('Erreur lors de la récupération des concours:', error);
      }
    };

    fetchContests();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'links') {
      fetchContests();
    }
  }, [activeTab, user]);

  const filteredContestants = contestants.filter(contestant =>
    contestant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    contestant.contest?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <ClientLayout>
      <div className="py-8 space-y-8">
        {/* En-tête avec statistiques */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6">
            {/* Titre et bouton */}
            <div className="flex items-center gap-4">
              <div className="relative">
                <div className="absolute -inset-2 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur opacity-75"></div>
                <Users className="h-8 w-8 text-yellow-400 relative" />
              </div>
              <h1 className="text-2xl font-bold">
                <span className="bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-200 text-transparent bg-clip-text">
                  Mes Candidats
                </span>
              </h1>
            </div>

            {/* Cartes de statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">Total Candidats</p>
                  <span className="text-2xl font-bold text-white">{stats.total}</span>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">Total Votes</p>
                  <span className="text-2xl font-bold text-yellow-400">{stats.totalVotes}</span>
                </div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-400">Moyenne Votes</p>
                  <span className="text-2xl font-bold text-green-400">{stats.averageVotes}</span>
                </div>
              </div>
            </div>

            {/* Onglets */}
            <div className="flex space-x-4 border-b border-white/10">
              <button
                className={`px-4 py-2 font-medium ${
                  activeTab === 'list'
                    ? 'text-yellow-400 border-b-2 border-yellow-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('list')}
              >
                Liste des candidats
              </button>
              <button
                className={`px-4 py-2 font-medium ${
                  activeTab === 'links'
                    ? 'text-yellow-400 border-b-2 border-yellow-400'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
                onClick={() => setActiveTab('links')}
              >
                Liens à copier
              </button>
            </div>

            {/* Contenu des onglets */}
            {activeTab === 'list' ? (
              <>
                {/* Filtres existants */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Input
                    type="text"
                    placeholder="Rechercher un candidat..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-white/5 border-white/10 text-white"
                  />
                  <Select
                    value={contestFilter}
                    onValueChange={setContestFilter}
                    options={contestOptions}
                  />
                  <Select
                    value={sortBy}
                    onValueChange={setSortBy}
                    options={sortOptions}
                  />
                  <Select
                    value={dateFilter}
                    onValueChange={setDateFilter}
                    options={dateOptions}
                  />
                </div>

                {/* Liste des candidats existante */}
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-lg">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-white" />
                    </div>
                  ) : contestants.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-400">Aucun candidat trouvé</p>
                    </div>
                  ) : (
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                      {filteredContestants.map((contestant) => (
                        <a
                          key={contestant.id}
                          href={`/${contestant.unique_code}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group relative overflow-hidden rounded-xl bg-gradient-to-br from-white/5 to-white/10 p-4 transition-all hover:from-white/10 hover:to-white/20"
                        >
                          <div className="aspect-square overflow-hidden rounded-lg bg-gray-800">
                            <img
                              src={contestant.photo_url || '/placeholder.png'}
                              alt={contestant.name}
                              className="h-full w-full object-cover transition-all group-hover:scale-105"
                            />
                          </div>
                          <div className="mt-4">
                            <h3 className="font-semibold text-white">{contestant.name}</h3>
                            <p className="text-sm text-gray-400">{contestant.contest.name}</p>
                            <div className="mt-2 flex items-center justify-between">
                              <p className="text-sm text-gray-400">Code: {contestant.unique_code}</p>
                              <p className="text-sm font-medium text-yellow-400">{contestant.votes_count} votes</p>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  )}

                  {/* Pagination */}
                  <div className="mt-6 flex items-center justify-between">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-2 text-sm text-gray-400 transition-colors hover:text-white disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Précédent
                    </button>
                    <span className="text-sm text-gray-400">
                      Page {currentPage} sur {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-2 text-sm text-gray-400 transition-colors hover:text-white disabled:opacity-50"
                    >
                      Suivant
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                {/* Liste des concours */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contestsData.map(contest => (
                    <button
                      key={contest.name}
                      onClick={() => setSelectedContest(contest.name)}
                      className={`p-4 rounded-lg text-left transition-colors ${
                        selectedContest === contest.name
                          ? 'bg-yellow-400/20 border border-yellow-400/50'
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <h3 className="font-medium text-white">{contest.name}</h3>
                      <p className="text-sm text-gray-400">
                        {contest.contestants.length} candidat{contest.contestants.length > 1 ? 's' : ''}
                      </p>
                    </button>
                  ))}
                </div>

                {/* Affichage des liens */}
                {selectedContest && (
                  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-medium text-white">{selectedContest}</h3>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(getContestLinks(selectedContest))}
                      >
                        {copied ? (
                          <Check className="h-4 w-4 mr-2" />
                        ) : (
                          <Copy className="h-4 w-4 mr-2" />
                        )}
                        {copied ? 'Copié !' : 'Copier'}
                      </Button>
                    </div>
                    <pre className="bg-black/20 p-4 rounded-lg text-gray-300 whitespace-pre-wrap font-mono text-sm">
                      {getContestLinks(selectedContest)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </ClientLayout>
  );
};

export default ContestantList;
